// FuelingHub — authenticated hub at /equipment (canonical, post 2026-05-06
// rename — was /fueling, which now aliases). Shows category clusters of
// equipment tiles + a Quick Fuel Log tile + a Fuel Supply tile. Each tile
// navigates to /equipment/<slug> or /equipment/supply.
//
// Own sub-routing under /equipment/* just like WebformHub owns /dailys/*.
import React from 'react';
import {useAppLocation as useLocation, useAppNavigate as useNavigate} from '../app/navigation/index.js';
import {EQUIPMENT_CATEGORIES} from '../lib/equipment.js';
import type {EquipmentCategory} from '../lib/equipment.js';
import EquipmentCategoryIcon from '../components/EquipmentCategoryIcon.jsx';
import PlannerIcon from '../components/PlannerIcon.jsx';
import EquipmentFuelingWebform from './EquipmentFuelingWebform.tsx';
import FuelSupplyWebform from './FuelSupplyWebform.jsx';
import AppSetupModal from './AppSetupModal.jsx';
import {sb} from '../lib/supabase.js';
import {RESOURCE} from '../app/data/routeDataCatalog.js';
import type {EquipmentFuelingHubData} from '../app/data/routeDataCatalog.js';
import {useRouteResource} from '../app/data/useRouteResource.js';
import type {SessionSubmitter} from './webformsRoutes.js';
import Button from '../shared/Button.js';

type PublicEquipmentRow = {
  id: string;
  name: string;
  slug: string;
  category: string;
  tracking_unit?: string | null;
  fuel_type?: string | null;
  current_hours?: number | string | null;
  current_km?: number | string | null;
};

type FuelingHubProps = {
  sessionSubmitter?: SessionSubmitter;
  ownerReports?: React.ReactNode;
};

type EquipmentGroup = EquipmentCategory & {
  rows: PublicEquipmentRow[];
};

const FUELING_HUB_PARAMS = Object.freeze({});

export default function FuelingHub({sessionSubmitter, ownerReports}: FuelingHubProps) {
  const [showAppSetup, setShowAppSetup] = React.useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  // Sub-routing from pathname.
  const path = location.pathname;
  const showOwnReports = path === '/equipment' && new URLSearchParams(location.search).get('reports') === 'mine';
  let subRoute: 'hub' | 'supply' | 'form' = 'hub';
  let slug: string | null = null;
  if (path === '/equipment/supply') subRoute = 'supply';
  else if (path.startsWith('/equipment/')) {
    slug = path.slice('/equipment/'.length);
    subRoute = 'form';
  }

  // One scoped read owns the tiles AND the per-machine form's equipment/
  // equipmentList. Disabled on /equipment/supply: FuelSupplyWebform consumes
  // nothing from it and is dispatched above this read's screens, so the query
  // that used to fire and be thrown away on that route now does not fire at all.
  const hubResource = useRouteResource(RESOURCE.EQUIPMENT_FUELING_HUB_CORE, FUELING_HUB_PARAMS, {
    enabled: subRoute !== 'supply' && !showOwnReports,
  });
  const hubData = hubResource.data as EquipmentFuelingHubData | null;
  const equipment = (hubData?.equipment || []) as PublicEquipmentRow[];
  const missingSchema = hubData?.missingSchema === true;
  const loading = hubResource.loading;

  const wfBg: React.CSSProperties = {
    minHeight: '100vh',
    background: 'var(--bg-page)',
    padding: '1rem',
    fontFamily: 'inherit',
  };
  const logoEl = (
    <div style={{textAlign: 'center', marginBottom: 20}}>
      <a
        data-public-hub-main-link="equipment"
        href="/"
        style={{
          fontSize: 28,
          fontWeight: 800,
          color: 'var(--ink)',
          letterSpacing: -0.3,
          textDecoration: 'none',
        }}
      >
        WCF Planner
      </a>
    </div>
  );

  // FUEL SUPPLY DISPATCHES FIRST, BEFORE THE EQUIPMENT READ IS CONSULTED.
  //
  // FuelSupplyWebform records deliveries into the bulk tank. It takes no
  // equipment prop, renders no equipment, and reads no equipment table — it is
  // the one subroute of this hub that is completely independent of the query
  // above. Yet it used to be selected AFTER both the `missingSchema` screen and
  // the `loading` screen, so opening /equipment/supply painted a full-page
  // "Loading…" and waited on a network read whose result it then ignored.
  //
  // Hoisting it here is the whole fix: the form paints on the first frame.
  //
  // ABOVE `missingSchema` deliberately, not merely above `loading`. Both flags
  // are set by the same effect, so at first paint missingSchema is false and
  // loading is true; sitting between them would render the form and then, if
  // the equipment table turned out to be absent, REPLACE it with the migration
  // notice — unmounting a form the user may already be typing into. A late
  // arriving flag must not evict a mounted surface. The notice is about the
  // equipment tables specifically (migration 016) and has nothing to say about
  // fuel supply, so it should not gate this route at all.
  if (subRoute === 'supply') {
    return <FuelSupplyWebform onBack={() => navigate('/equipment')} sessionSubmitter={sessionSubmitter} />;
  }

  if (showOwnReports) return <>{ownerReports}</>;

  if (missingSchema) {
    return (
      <div style={wfBg}>
        <div style={{maxWidth: 480, margin: '0 auto', paddingTop: '1rem'}}>
          {logoEl}
          <div
            style={{
              background: 'var(--warn-soft)',
              border: '1px solid var(--warn-stripe)',
              borderRadius: 12,
              padding: '1rem',
              color: 'var(--warn-ink)',
              fontSize: 13,
            }}
          >
            Equipment tables aren't set up yet. Ask an admin to finish setup.
          </div>
        </div>
      </div>
    );
  }
  if (hubResource.error) {
    return (
      <div style={wfBg}>
        <div style={{maxWidth: 480, margin: '0 auto', paddingTop: '1rem'}}>
          {logoEl}
          <div
            data-equipment-hub-error="1"
            style={{
              background: 'var(--danger-soft)',
              border: '1px solid var(--danger)',
              borderRadius: 12,
              padding: '1rem',
              color: 'var(--danger-ink)',
              fontSize: 13,
            }}
          >
            Could not load equipment. Check your connection and try again.
            <div>
              <button
                onClick={() => hubResource.refetch()}
                style={{
                  marginTop: 10,
                  color: 'var(--brand)',
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  textDecoration: 'underline',
                  fontFamily: 'inherit',
                }}
              >
                Retry
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }
  if (loading)
    return (
      <div style={wfBg}>
        <div style={{maxWidth: 480, margin: '0 auto', paddingTop: '1rem'}}>
          {logoEl}
          <div style={{textAlign: 'center', color: 'var(--ink-faint)'}}>Loading{'…'}</div>
        </div>
      </div>
    );

  if (subRoute === 'form') {
    const eq = equipment.find((e) => e.slug === slug);
    if (!eq) {
      return (
        <div style={wfBg}>
          <div style={{maxWidth: 480, margin: '0 auto', paddingTop: '1rem'}}>
            {logoEl}
            <div
              style={{
                background: 'white',
                border: '1px solid var(--border)',
                borderRadius: 12,
                padding: '1rem',
                textAlign: 'center',
                color: 'var(--ink-muted)',
              }}
            >
              No equipment with slug <code>{slug}</code>.
              <div>
                <button
                  onClick={() => navigate('/equipment')}
                  style={{
                    marginTop: 10,
                    color: 'var(--brand)',
                    background: 'none',
                    border: 'none',
                    cursor: 'pointer',
                    textDecoration: 'underline',
                    fontFamily: 'inherit',
                  }}
                >
                  ← Back to hub
                </button>
              </div>
            </div>
          </div>
        </div>
      );
    }
    return (
      <EquipmentFuelingWebform
        sb={sb}
        equipment={eq}
        equipmentList={equipment}
        onBack={() => navigate('/equipment')}
        sessionSubmitter={sessionSubmitter}
      />
    );
  }

  // (The 'supply' subroute is dispatched at the top of this function, above the
  // equipment read's loading and missing-schema screens — see the comment there.)

  // HUB — category clusters
  // Q35: fixed values render through one Title Case label map.
  const FUEL_TYPE_LABELS: Record<string, string> = {diesel: 'Diesel', gasoline: 'Gasoline'};
  const grouped: EquipmentGroup[] = EQUIPMENT_CATEGORIES.map((cat) => ({
    ...cat,
    rows: equipment.filter((e) => e.category === cat.key),
  })).filter((g) => g.rows.length > 0);

  return (
    <div style={wfBg}>
      {showAppSetup && <AppSetupModal onClose={() => setShowAppSetup(false)} />}
      <div style={{maxWidth: 720, margin: '0 auto', paddingTop: '1rem'}}>
        {logoEl}
        <div style={{display: 'flex', justifyContent: 'center', marginBottom: 12}}>
          <Button data-my-equipment-reports="1" onClick={() => navigate('/equipment?reports=mine')}>
            My Equipment Reports
          </Button>
          <button
            data-app-setup-trigger="1"
            onClick={() => setShowAppSetup(true)}
            style={{
              background: 'white',
              border: '1px solid var(--border-strong)',
              borderRadius: 999,
              padding: '6px 14px',
              fontSize: 12,
              fontWeight: 600,
              color: 'var(--ink)',
              cursor: 'pointer',
              fontFamily: 'inherit',
            }}
          >
            App Setup
          </button>
        </div>
        <div style={{fontSize: 13, color: 'var(--ink-muted)', textAlign: 'center', marginBottom: 20}}>
          Tap your equipment to log a fueling
        </div>

        {grouped.map((g) => (
          <div key={g.key} style={{marginBottom: 18}}>
            <div style={{display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8, paddingLeft: 4}}>
              <EquipmentCategoryIcon category={g} size={20} />
              <span
                style={{
                  fontSize: 15,
                  fontWeight: 700,
                  color: 'var(--ink)',
                }}
              >
                {g.label}
              </span>
            </div>
            <div style={{display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))', gap: 8}}>
              {g.rows.map((eq) => (
                <button
                  key={eq.id}
                  onClick={() => navigate('/equipment/' + eq.slug)}
                  className="wcf-clickable"
                  style={{
                    background: 'white',
                    border: '1px solid var(--border)',
                    borderRadius: 18,
                    padding: '14px 14px',
                    textAlign: 'left',
                    cursor: 'pointer',
                    fontFamily: 'inherit',
                  }}
                >
                  <div style={{fontSize: 14, fontWeight: 700, color: 'var(--ink)', marginBottom: 2}}>{eq.name}</div>
                  <div style={{fontSize: 11, color: 'var(--text-2)'}}>
                    {eq.tracking_unit === 'km' ? (eq.current_km || '?') + ' km' : (eq.current_hours || '?') + ' hr'}
                    {eq.fuel_type ? ' · ' + (FUEL_TYPE_LABELS[eq.fuel_type] || eq.fuel_type) : ''}
                  </div>
                </button>
              ))}
            </div>
          </div>
        ))}

        <div style={{marginTop: 20, marginBottom: 12}}>
          <div style={{display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8, paddingLeft: 4}}>
            <PlannerIcon iconKey="fueling" size={20} />
            <span
              style={{
                fontSize: 15,
                fontWeight: 700,
                color: 'var(--ink)',
              }}
            >
              Other
            </span>
          </div>
          <div style={{display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: 8}}>
            <button
              onClick={() => navigate('/equipment/supply')}
              className="wcf-clickable"
              style={{
                background: 'white',
                border: '1px solid var(--border)',
                borderRadius: 18,
                padding: '14px 14px',
                textAlign: 'left',
                cursor: 'pointer',
                fontFamily: 'inherit',
              }}
            >
              <div
                style={{
                  fontSize: 14,
                  fontWeight: 700,
                  color: 'var(--ink)',
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 6,
                }}
              >
                <PlannerIcon iconKey="fueling" size={18} />
                <span>Fuel Supply Log</span>
              </div>
              <div style={{fontSize: 11, color: 'var(--text-2)'}}>Mobile fuel cell, gas cans, off-checklist trucks</div>
            </button>
          </div>
        </div>

        <div style={{textAlign: 'center', marginTop: 16}}>
          <button type="button" className="wcf-back-link" onClick={() => navigate('/dailys')}>
            <span aria-hidden="true">‹</span> Back
          </button>
        </div>
      </div>
    </div>
  );
}
